import React, { Fragment, useState } from "react";
import "./header.scss";
import { DateTime } from "luxon";
import { MOBILE_BUSINESS_HOURS, SALON_BUSINESS_HOURS } from "../../constants/businessHours";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { NavLink, useNavigate } from "react-router-dom";
import { SOCIALS } from "../../constants/socials";
import {
  faArrowUpRightFromSquare,
  faBars,
  faChevronDown,
  faChevronUp,
  faLocationPin,
  faPhone,
  faSquarePhone,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { useWindowSize } from "../../hooks/useWindowSize";
import { TABLET_WIDTH } from "../../constants/screenSizes";
import classNames from "classnames";
import Divider from "../Divider/Divider";
import { useBusinessHours } from "../../context/businessHoursContext";

export type TMenuItem = {
  label: string;
  url: string;
  subOptions?: Array<Omit<TMenuItem, "subOptions">>;
};

interface IMobileMenu {
  open: boolean;
}

export const MENU_ITEMS_LEFT: Array<TMenuItem> = [
  {
    label: "Salon Services",
    url: "/salon-services",
  },
  { label: "Mobile Services", url: "/mobile-services" },
  {
    label: "Other Services",
    url: "/",
    subOptions: [
      // { label: "Lash Extensions", url: "/lash-extensions" },
      { label: "Vendor", url: "/vendors" },
    ],
  },
];

export const MENU_ITEMS_RIGHT: Array<TMenuItem> = [
  { label: "Comfort Wellness", url: "/wellness" },
  { label: "Events", url: "/events" },
  { label: "About", url: "/about" },
  { label: "Contact", url: "/contact" },
];

const today = DateTime.now();
//SALON HOURS
const TODAY_BUSINESS_HOURS_SALON = SALON_BUSINESS_HOURS[today.weekdayLong.toLowerCase()];
const TODAY_OPEN_SALON = DateTime.fromFormat(TODAY_BUSINESS_HOURS_SALON?.open, "hh:mm")?.toFormat("h a");
const TODAY_CLOSED_SALON = DateTime.fromFormat(TODAY_BUSINESS_HOURS_SALON?.close, "hh:mm")?.toFormat("h a");

// //MOBILE SALON HOURS
const TODAY_BUSINESS_HOURS_MOBILE = MOBILE_BUSINESS_HOURS[today.weekdayLong.toLowerCase()];
const TODAY_OPEN_MOBILE = DateTime.fromFormat(TODAY_BUSINESS_HOURS_MOBILE?.open, "hh:mm")?.toFormat("h a");
const TODAY_CLOSED_MOBILE = DateTime.fromFormat(TODAY_BUSINESS_HOURS_MOBILE?.close, "hh:mm")?.toFormat("h a");
export default function Header() {
  const navigate = useNavigate();
  const windowSize = useWindowSize();
  const businessHours = useBusinessHours();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  function setActive(e: React.MouseEvent, optionIndex: number | null) {
    e.stopPropagation();
    setActiveIndex(optionIndex);
  }
  const [mobileMenu, setMobileMenu] = useState<IMobileMenu>({
    open: false,
  });
  function handleNavigate(url: string, newTab = false) {
    if (newTab) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  }

  function navigateToHome() {
    handleNavigate("/");
    setMobileMenu(prevState => ({ ...prevState, open: false }));
  }

  return (
    <Fragment>
      <div className="header-primary">
        <div className="header-primary-inner">
          <div className="header-primary-hours-container">
            <div>
              {businessHours && businessHours?.length > 0 ? (
                businessHours?.map((location, index) => {
                  const TODAY_BUSINESS_HOURS = location?.hours[today.weekdayLong.toLowerCase()];
                  const TODAY_OPEN = DateTime.fromFormat(TODAY_BUSINESS_HOURS?.open, "hh:mm")?.toFormat("h a");
                  const TODAY_CLOSED = DateTime.fromFormat(TODAY_BUSINESS_HOURS?.close, "hh:mm")?.toFormat("h a");
                  return (
                    <div className="header-primary-group" key={index}>
                      <span className="header-primary-text">{location?.title}:</span>
                      {TODAY_BUSINESS_HOURS?.closed ? (
                        <span className="header-primary-text">Closed | {today.weekdayLong}</span>
                      ) : (
                        <span className="header-primary-text">
                          Open | {today.weekdayLong} {TODAY_OPEN} - {TODAY_CLOSED}
                        </span>
                      )}
                    </div>
                  );
                })
              ) : (
                <>
                  <div className="header-primary-group">
                    <span className="header-primary-text">Salon:</span>
                    {TODAY_BUSINESS_HOURS_SALON?.closed ? (
                      <span className="header-primary-text">Closed | {today.weekdayLong}</span>
                    ) : (
                      <span className="header-primary-text">
                        Open | {today.weekdayLong} {TODAY_OPEN_SALON} - {TODAY_CLOSED_SALON}
                      </span>
                    )}
                  </div>

                  <div className="header-primary-group">
                    <span className="header-primary-text">Mobile Salon:</span>
                    {TODAY_BUSINESS_HOURS_MOBILE?.closed ? (
                      <span className="header-primary-text">Closed | {today.weekdayLong}</span>
                    ) : (
                      <span className="header-primary-text">
                        Open | {today.weekdayLong} {TODAY_OPEN_MOBILE} - {TODAY_CLOSED_MOBILE}
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="header-primary-hours-container">
              {SOCIALS?.map((social, index) => (
                <div
                  key={index}
                  className="header-primary-socials-container"
                  onClick={() => handleNavigate(social.url, true)}
                >
                  <FontAwesomeIcon fixedWidth className="header-primary-socials" icon={social.icon} />
                </div>
              ))}
            </div>
          </div>

          <div className="header-primary-group">
            <div>
              <a
                className="header-primary-text header-primary-link"
                href="https://maps.app.goo.gl/BoLfQjvqA9NyZ4zS6"
                target="_blank"
                rel="noreferrer"
              >
                1651 Erbs Rd, St. Agatha, ON N0B 2L0
              </a>
              <p>
                <a
                  className="header-primary-text-booking header-primary-link"
                  href="https://book.squareup.com/appointments/i0gxiws9yfwy9y/location/LYCYRVYNYY0FC/services"
                  target="_blank"
                  rel="noreferrer"
                >
                  Book Online <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                </a>
              </p>
            </div>
            <div>
              <a className="header-primary-text header-primary-link" href="tel:519-496-1246">
                <FontAwesomeIcon icon={faPhone} /> +1 519-496-1246
              </a>
            </div>
          </div>
          {/* <img src="images/logo.jpg"/> */}
        </div>
      </div>
      {/* Mobile Menu */}
      {windowSize.width <= TABLET_WIDTH ? (
        <header className="header-mobile-menu header-secondary">
          <div className="header-secondary-inner">
            <h1 className="header-secondary-title noselect" onClick={() => handleNavigate("/")}>
              <span>Comfort Cutz</span>
              <span className="header-secondary-title-sub-text">Beauty & Wellness</span>
            </h1>
            <FontAwesomeIcon
              onClick={() => setMobileMenu(prevState => ({ ...prevState, open: !prevState.open }))}
              icon={faBars}
              size="2x"
              className="header-mobile-menu-icon"
            />
          </div>
          <div
            onClick={() => setMobileMenu(prevState => ({ ...prevState, open: !prevState.open }))}
            className={classNames("mobile-menu-backdrop", { "mobile-menu-backdrop-visible": mobileMenu.open })}
          ></div>
          <div
            className={"mobile-menu-container"}
            style={{
              width: mobileMenu.open ? "80%" : undefined,
              boxShadow: mobileMenu.open ? "rgba(0, 0, 0, 0.56) 0px 22px 70px 4px" : undefined,
            }}
          >
            <div
              className={classNames("mobile-menu-inner-container", {
                "mobile-menu-inner-container-hidden": !mobileMenu.open,
              })}
            >
              <div>
                <div className="mobile-menu-header-container">
                  <div className="image-logo-container">
                    <img
                      src="/images/logo.webp"
                      alt="Comfort Cutz logo"
                      className="image-logo"
                      onClick={() => navigateToHome()}
                    />
                  </div>
                  <div className="mobile-menu-header-container-inner">
                    {businessHours && businessHours?.length > 0 ? (
                      businessHours?.map((location, index) => {
                        const TODAY_BUSINESS_HOURS = location?.hours[today.weekdayLong.toLowerCase()];
                        const TODAY_OPEN = DateTime.fromFormat(TODAY_BUSINESS_HOURS?.open, "hh:mm")?.toFormat("h a");
                        const TODAY_CLOSED = DateTime.fromFormat(TODAY_BUSINESS_HOURS?.close, "hh:mm")?.toFormat("h a");
                        return TODAY_BUSINESS_HOURS?.closed ? (
                          <span className="mobile-menu-header-container-inner-text" key={index}>
                            {location?.title}: Closed | {today.weekdayLong}
                          </span>
                        ) : (
                          <span className="mobile-menu-header-container-inner-text" key={index}>
                            {location?.title}: Open | {today.weekdayLong} {TODAY_OPEN} - {TODAY_CLOSED}
                          </span>
                        );
                      })
                    ) : (
                      <>
                        {/* SALON HOURS */}
                        {TODAY_BUSINESS_HOURS_SALON?.closed ? (
                          <span className="mobile-menu-header-container-inner-text">
                            Salon: Closed | {today.weekdayLong}
                          </span>
                        ) : (
                          <span className="mobile-menu-header-container-inner-text">
                            Salon: Open | {today.weekdayLong} {TODAY_OPEN_SALON} - {TODAY_CLOSED_SALON}
                          </span>
                        )}

                        {/* MOBILE SALON HOURS */}
                        {TODAY_BUSINESS_HOURS_MOBILE?.closed ? (
                          <span className="mobile-menu-header-container-inner-text">
                            Mobile Salon: Closed | {today.weekdayLong}
                          </span>
                        ) : (
                          <span className="mobile-menu-header-container-inner-text">
                            Mobile Salon: Open | {today.weekdayLong} {TODAY_OPEN_MOBILE} - {TODAY_CLOSED_MOBILE}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  <FontAwesomeIcon
                    icon={faXmark}
                    className="mobile-menu-close-button"
                    onClick={() => setMobileMenu(prevState => ({ ...prevState, open: !prevState.open }))}
                  />
                </div>
                <div className="mobile-menu-options-container">
                  <p className="option-header">Menu</p>
                  {MENU_ITEMS_LEFT?.map((item, index) => {
                    if (!item?.subOptions) {
                      return (
                        <NavLink
                          key={index}
                          to={item.url}
                          onClick={() => setMobileMenu(prevState => ({ ...prevState, open: !prevState.open }))}
                          className={({ isActive, isPending, isTransitioning }) =>
                            [isActive ? "menu-item-active" : "", "menu-item"].join(" ")
                          }
                        >
                          {item?.label}
                        </NavLink>
                      );
                    } else if (item?.subOptions) {
                      //Menu Options with suboptions
                      return (
                        <div
                          className="menu-item-main-sub menu-item-mobile"
                          key={index}
                          onMouseEnter={e => setActive(e, index)}
                          onMouseLeave={e => setActive(e, null)}
                        >
                          <div className="menu-item-mobile-title">
                            <p className="menu-item">{item?.label}</p>
                            <FontAwesomeIcon icon={activeIndex === index ? faChevronUp : faChevronDown} />
                          </div>

                          <div
                            className={classNames("menu-item-sub-menu menu-item-sub-menu-mobile", {
                              "menu-item-sub-menu-active": activeIndex === index,
                            })}
                          >
                            {item?.subOptions?.map((subOption, subOptionIndex) => {
                              return (
                                <NavLink
                                  key={subOptionIndex}
                                  to={subOption.url ?? "/"}
                                  onClick={e => {
                                    setActive(e, null);
                                    setMobileMenu(prevState => ({ ...prevState, open: !prevState.open }));
                                  }}
                                  className={({ isActive, isPending, isTransitioning }) =>
                                    [isActive ? "menu-item-active" : "", "menu-item"].join(" ")
                                  }
                                >
                                  <p>{subOption?.label}</p>
                                </NavLink>
                              );
                            })}
                          </div>
                        </div>
                      );
                    }
                  })}
                </div>
                <Divider position="horizontal" colour="secondary" />
                <div className="mobile-menu-options-container">
                  <p className="option-header">Company</p>
                  {MENU_ITEMS_RIGHT?.map((item, index) => {
                    if (!item?.subOptions) {
                      return (
                        <NavLink
                          key={index}
                          to={item.url}
                          onClick={() => setMobileMenu(prevState => ({ ...prevState, open: !prevState.open }))}
                          className={({ isActive, isPending, isTransitioning }) =>
                            [isActive ? "menu-item-active" : "", "menu-item"].join(" ")
                          }
                        >
                          {item?.label}
                        </NavLink>
                      );
                    } else if (item?.subOptions) {
                      //Menu Options with suboptions
                      return (
                        <div
                          className="menu-item-main-sub menu-item-mobile"
                          key={index}
                          onMouseEnter={e => setActive(e, index)}
                          onMouseLeave={e => setActive(e, null)}
                        >
                          <div className="menu-item-mobile-title">
                            <p className="menu-item">{item?.label}</p>
                            <FontAwesomeIcon icon={activeIndex === index ? faChevronUp : faChevronDown} />
                          </div>

                          <div
                            className={classNames("menu-item-sub-menu menu-item-sub-menu-mobile", {
                              "menu-item-sub-menu-active": activeIndex === index,
                            })}
                          >
                            {item?.subOptions?.map((subOption, subOptionIndex) => {
                              return (
                                <NavLink
                                  key={subOptionIndex}
                                  to={subOption.url ?? "/"}
                                  onClick={e => {
                                    setActive(e, null);
                                    setMobileMenu(prevState => ({ ...prevState, open: !prevState.open }));
                                  }}
                                  className={({ isActive, isPending, isTransitioning }) =>
                                    [isActive ? "menu-item-active" : "", "menu-item"].join(" ")
                                  }
                                >
                                  <p>{subOption?.label}</p>
                                </NavLink>
                              );
                            })}
                          </div>
                        </div>
                      );
                    }
                  })}
                </div>
              </div>
              <div className="mobile-menu-footer-container">
                <div className="mobile-menu-footer-container-details">
                  <a
                    className="mobile-menu-footer-container-text"
                    href="https://book.squareup.com/appointments/i0gxiws9yfwy9y/location/LYCYRVYNYY0FC/services"
                    target="_blank"
                    rel="noreferrer"
                    style={{ marginBottom: "1rem" }}
                  >
                    Book Online <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                  </a>
                  <a href="tel:519-496-1246" className="mobile-menu-footer-container-text">
                    <FontAwesomeIcon className="mobile-menu-footer-container-text" size="1x" icon={faSquarePhone} />{" "}
                    {"+1 519-496-1246"}
                  </a>
                  <a
                    className="mobile-menu-footer-container-text"
                    href="https://maps.app.goo.gl/BoLfQjvqA9NyZ4zS6"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <FontAwesomeIcon className="mobile-menu-footer-container-text" size="1x" icon={faLocationPin} />{" "}
                    1651 Erbs Rd, St. Agatha, ON N0B 2L0
                  </a>
                </div>
                <div className="mobile-menu-socials-container">
                  {SOCIALS?.map((social, index) => (
                    <FontAwesomeIcon
                      key={index}
                      icon={social.icon}
                      className="socials-icon"
                      onClick={() => handleNavigate(social.url, true)}
                    />
                  ))}
                </div>
                <p className="mobile-menu-copyright">© {new Date().getFullYear()} Comfort Cutz. All rights reserved</p>
              </div>
            </div>
          </div>
        </header>
      ) : (
        // Desktop Menu
        <header className="header-secondary">
          <div className="header-secondary-inner">
            <div className="header-secondary-menu-group">
              {MENU_ITEMS_LEFT?.map((item, index) => {
                if (!item?.subOptions) {
                  return (
                    <NavLink
                      key={index}
                      to={item.url}
                      className={({ isActive, isPending, isTransitioning }) =>
                        [isActive ? "menu-item-active" : "", "menu-item"].join(" ")
                      }
                    >
                      {item?.label}
                    </NavLink>
                  );
                } else if (item?.subOptions) {
                  //Menu Options with suboptions
                  return (
                    <div
                      className="menu-item-main-sub "
                      key={index}
                      onMouseEnter={e => setActive(e, index)}
                      onMouseLeave={e => setActive(e, null)}
                    >
                      <p>{item?.label}</p>

                      <div
                        className={classNames("menu-item-sub-menu", {
                          "menu-item-sub-menu-active": activeIndex === index,
                        })}
                      >
                        {item?.subOptions?.map((subOption, subOptionIndex) => {
                          return (
                            <NavLink
                              key={subOptionIndex}
                              to={subOption.url ?? "/"}
                              onClick={e => setActive(e, null)}
                              className={({ isActive, isPending, isTransitioning }) =>
                                [isActive ? "menu-item-active" : "", "menu-item"].join(" ")
                              }
                            >
                              <p>{subOption?.label}</p>
                            </NavLink>
                          );
                        })}
                      </div>
                    </div>
                  );
                }
              })}
            </div>
            <h1 className="header-secondary-title noselect" onClick={() => handleNavigate("/")}>
              <span>Comfort Cutz</span>
              <span className="header-secondary-title-sub-text">Beauty & Wellness</span>
            </h1>
            <div className="header-secondary-menu-group">
              {MENU_ITEMS_RIGHT?.map((item, index) => {
                if (!item?.subOptions) {
                  return (
                    <NavLink
                      key={index}
                      to={item.url}
                      className={({ isActive, isPending, isTransitioning }) =>
                        [isActive ? "menu-item-active" : "", "menu-item"].join(" ")
                      }
                    >
                      {item?.label}
                    </NavLink>
                  );
                } else if (item?.subOptions) {
                  //Menu Options with suboptions
                  return (
                    <div
                      className="menu-item-main-sub "
                      key={index}
                      onMouseEnter={e => setActive(e, index)}
                      onMouseLeave={e => setActive(e, null)}
                    >
                      <p>{item?.label}</p>

                      <div
                        className={classNames("menu-item-sub-menu", {
                          "menu-item-sub-menu-active": activeIndex === index,
                        })}
                      >
                        {item?.subOptions?.map((subOption, subOptionIndex) => {
                          return (
                            <NavLink
                              key={subOptionIndex}
                              to={subOption.url ?? "/"}
                              onClick={e => setActive(e, null)}
                              className={({ isActive, isPending, isTransitioning }) =>
                                [isActive ? "menu-item-active" : "", "menu-item"].join(" ")
                              }
                            >
                              <p>{subOption?.label}</p>
                            </NavLink>
                          );
                        })}
                      </div>
                    </div>
                  );
                }
              })}
            </div>
          </div>
        </header>
      )}
    </Fragment>
  );
}
